import { useState, useEffect } from "react";
import { useEOSAuth } from "../../contexts/EOSAuthContext";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_URL || "https://engineeringofstockbackend.onrender.com";

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
});

export type EngineStatus = {
  isRunning: boolean;
  totalModels: number;
  appliedModels: Array<{ name: string; [key: string]: unknown }>;
  activeModels: string[];
};

export type MarketStatus = {
  isOpen: boolean;
  [key: string]: unknown;
};

export function useTradingControl() {
  const { token } = useEOSAuth();
  const [engineStatus, setEngineStatus] = useState<EngineStatus | null>(null);
  const [marketStatus, setMarketStatus] = useState<MarketStatus | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  useEffect(() => {
    if (token) {
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.common["Authorization"];
    }
  }, [token]);

  const fetchMarketStatus = async () => {
    try {
      const response = await api.get("/api/trading/market-status");

      if (response.data.success) {
        setMarketStatus(response.data.data);
      }
    } catch {
      // noop
    }
  };

  const fetchEngineStatus = async () => {
    try {
      const response = await api.get("/api/automated-trading/status");

      if (response.data.success) {
        setEngineStatus({
          isRunning: response.data.data.isActive,
          totalModels: 1,
          appliedModels: response.data.data.currentModel ? [response.data.data.currentModel] : [],
          activeModels: response.data.data.isActive ? [response.data.data.currentModel?.name].filter(Boolean) : [],
        });
        setLastUpdate(new Date());
      } else {
        setEngineStatus({
          isRunning: false,
          totalModels: 0,
          appliedModels: [],
          activeModels: [],
        });
      }
    } catch {
      setEngineStatus({
        isRunning: false,
        totalModels: 0,
        appliedModels: [],
        activeModels: [],
      });
    }
  };

  useEffect(() => {
    fetchEngineStatus();
    fetchMarketStatus();

    const interval = setInterval(() => {
      fetchEngineStatus();
      fetchMarketStatus();
    }, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startTrading = async () => {
    try {
      setIsStarting(true);
      setError(null);

      const response = await api.post("/api/automated-trading/start");

      if (response.data.success) {
        await fetchEngineStatus();
      } else {
        throw new Error(response.data?.error || "Failed to start automated trading");
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to start automated trading";
      setError(errorMessage);
    } finally {
      setIsStarting(false);
    }
  };

  const stopTrading = async () => {
    try {
      setIsStopping(true);
      setError(null);

      const response = await api.post("/api/automated-trading/stop");

      if (response.data.success) {
        await fetchEngineStatus();
      } else {
        throw new Error(response.data?.error || "Failed to stop automated trading");
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to stop automated trading";
      setError(errorMessage);
    } finally {
      setIsStopping(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "text-green-600";
      case "paused":
        return "text-yellow-600";
      case "inactive":
        return "text-gray-600";
      default:
        return "text-gray-600";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "active":
        return "check";
      case "paused":
        return "pause";
      case "inactive":
        return "clock";
      default:
        return "clock";
    }
  };

  return {
    engineStatus,
    marketStatus,
    isStarting,
    isStopping,
    error,
    lastUpdate,
    fetchEngineStatus,
    startTrading,
    stopTrading,
    getStatusColor,
    getStatusIcon,
  };
}
